import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useForumStore, useAuthStore } from '../../stores/root_store';
import { AuthorName } from '../../components/AuthorName';
import type { ForumActivity, ForumSectionWithActivity } from '../../types';

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

const ActivityItem = ({
  activity,
  sectionSlug,
}: {
  activity: ForumActivity;
  sectionSlug: string;
}) => {
  const isPost = activity.activity_type === 'post';

  // Build link URL
  let linkUrl = '/assistant/forum';
  if (isPost && activity.slug) {
    linkUrl = `/assistant/forum/${sectionSlug}/${activity.slug}`;
  } else if (!isPost && activity.post_slug) {
    linkUrl = `/assistant/forum/${sectionSlug}/${activity.post_slug}#comment-${activity.uuid}`;
  }

  return (
    <Box
      component={Link}
      to={linkUrl}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        py: 1,
        px: 1,
        borderRadius: 1,
        textDecoration: 'none',
        color: 'inherit',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ color: 'text.secondary', mt: 0.25 }}>
        {isPost ? (
          <ArticleOutlinedIcon fontSize="small" />
        ) : (
          <ChatBubbleOutlineIcon fontSize="small" />
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <AuthorName name={activity.author_name} userUuid={activity.user_uuid} fontWeight={500} />
          <Typography variant="caption" color="text.secondary">
            {isPost ? 'posted' : 'commented'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatRelativeTime(activity.activity_date)}
          </Typography>
        </Box>

        {isPost ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {activity.title}
            </Typography>
            {activity.label && (
              <Chip label={activity.label} size="small" color="primary" />
            )}
          </Box>
        ) : (
          <>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                mt: 0.25,
              }}
            >
              {activity.body}
            </Typography>
            {activity.post_title && (
              <Typography variant="caption" color="text.secondary">
                in "{activity.post_title}"
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

const SectionCard = ({ section }: { section: ForumSectionWithActivity }) => {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        {/* Section Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
          }}
        >
          <Box>
            <Typography
              component={Link}
              to={`/assistant/forum/${section.slug}`}
              variant="h6"
              sx={{
                textDecoration: 'none',
                color: 'inherit',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {section.title}
            </Typography>
            {section.description && (
              <Typography variant="body2" color="text.secondary">
                {section.description}
              </Typography>
            )}
          </Box>
          <Link
            to={`/assistant/forum/${section.slug}`}
            style={{ textDecoration: 'none' }}
          >
            <Typography
              variant="body2"
              color="primary"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                whiteSpace: 'nowrap',
              }}
            >
              View all <ArrowForwardIcon fontSize="small" />
            </Typography>
          </Link>
        </Box>

        <Divider sx={{ mb: 1 }} />

        {/* Recent Activity */}
        {section.recent_activity.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No recent activity in this section
          </Typography>
        ) : (
          <Stack spacing={0}>
            {section.recent_activity.map((activity) => (
              <ActivityItem
                key={activity.uuid}
                activity={activity}
                sectionSlug={section.slug}
              />
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export const ForumHomePage = observer(() => {
  const forumStore = useForumStore();
  const authStore = useAuthStore();

  useEffect(() => {
    if (authStore.initialized) {
      forumStore.fetchSectionsWithRecent(3);
    }
  }, [authStore.initialized, forumStore]);

  if (forumStore.sectionsWithActivityLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (forumStore.sectionsWithActivityError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {forumStore.sectionsWithActivityError}
      </Alert>
    );
  }

  if (forumStore.sectionsWithActivity.length === 0) {
    return (
      <Card>
        <CardContent>
          <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            <Typography color="text.secondary">
              No forum sections yet.
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Recent Activity
      </Typography>
      {forumStore.sectionsWithActivity.map((section) => (
        <SectionCard key={section.uuid} section={section} />
      ))}
    </Box>
  );
});
